import { useEffect, useId, useRef, type ElementType } from "react"
import Link from "next/link"

import { DESTINATION_FOCUS_RING_CLASS } from "@/app-shell/navigation/SidebarNavItemRow"
import {
  SidebarNavShortcutKeys,
  toAriaKeyShortcuts,
} from "@/app-shell/navigation/SidebarNavShortcutKeys"
import { Button, cn, Popover, PopoverAnchor, PopoverContent } from "@coval/ui"
import { Plus, Star } from "lucide-react"

interface DashboardNavItemProps {
  item: {
    href: string
    base_href: string
    icon?: ElementType
    shortcutKey?: string
    title: string
  }
  getHref: (path: string) => string
  isActive: (href: string, baseHref?: string) => boolean
  dashboards: { id: string; display_name: string; is_favorite: boolean }[]
  isLoading: boolean
  error?: Error | string
  onRetry: () => void
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

const MAX_STARRED_DASHBOARDS = 5
const OPEN_DELAY_MS = 150
const CLOSE_DELAY_MS = 200

export function DashboardNavItem({
  item,
  getHref,
  isActive,
  dashboards,
  isLoading,
  error,
  onRetry,
  isOpen,
  onOpenChange,
}: DashboardNavItemProps) {
  const Icon = item.icon
  const active = isActive(item.href, item.base_href)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const rowRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const popoverId = useId()
  const clearTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
  }
  const close = () => {
    clearTimer()
    onOpenChange(false)
  }
  const scheduleClose = () => {
    clearTimer()
    timer.current = setTimeout(() => {
      if (
        !rowRef.current?.contains(document.activeElement) &&
        !contentRef.current?.contains(document.activeElement)
      ) {
        onOpenChange(false)
      }
    }, CLOSE_DELAY_MS)
  }
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current)
      }
    },
    [],
  )
  const favorites = dashboards
    .filter((dashboard) => dashboard.is_favorite)
    .toSorted((a, b) => a.display_name.localeCompare(b.display_name))
    .slice(0, MAX_STARRED_DASHBOARDS)
  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>
        <div
          role="presentation"
          ref={rowRef}
          onFocus={() => {
            clearTimer()
            onOpenChange(true)
          }}
          onBlur={scheduleClose}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              if (isOpen) {
                event.preventDefault()
              }
              close()
            } else if (
              event.key === "ArrowRight" ||
              event.key === "ArrowDown"
            ) {
              event.preventDefault()
              contentRef.current
                ?.querySelector<HTMLElement>("a, button")
                ?.focus()
            }
          }}
          // The destination link fills the row but not its padding, so the row
          // draws the ring, on the same box as the other sidebar rows.
          className={cn(
            "group flex items-center gap-2 rounded-sm px-2 py-1 pr-1 transition-colors hover:bg-surface-1",
            active && "bg-surface-1",
            DESTINATION_FOCUS_RING_CLASS,
          )}
          onPointerEnter={(event) => {
            if (event.pointerType !== "mouse") {
              return
            }
            clearTimer()
            timer.current = setTimeout(() => {
              onOpenChange(true)
            }, OPEN_DELAY_MS)
          }}
          onPointerLeave={scheduleClose}
        >
          <Link
            href={item.href}
            onClick={close}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            aria-controls={isOpen ? popoverId : undefined}
            aria-current={active ? "page" : undefined}
            aria-keyshortcuts={
              item.shortcutKey
                ? toAriaKeyShortcuts(item.shortcutKey)
                : undefined
            }
            className="coval-custom-focus-ring coval-ancestor-focus-ring flex min-w-0 flex-1 items-center gap-2 text-sm font-medium focus-visible:outline-none"
          >
            {Icon && <Icon className="size-4 shrink-0" />}
            <span className="min-w-0 flex-1 truncate">{item.title}</span>
            {item.shortcutKey && (
              <SidebarNavShortcutKeys shortcutKey={item.shortcutKey} />
            )}
          </Link>
          <Link
            href={getHref("/dashboard?create=true")}
            onClick={close}
            aria-label="New Dashboard"
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-coval-text-muted opacity-0 transition-opacity hover:bg-surface-2 hover:text-coval-text group-hover:opacity-100 focus-visible:opacity-100"
          >
            <Plus className="h-3.5 w-3.5" />
          </Link>
        </div>
      </PopoverAnchor>
      <PopoverContent
        ref={contentRef}
        id={popoverId}
        aria-label="Starred dashboards"
        side="right"
        align="start"
        sideOffset={4}
        className="w-64 p-1"
        onPointerEnter={clearTimer}
        onPointerLeave={scheduleClose}
        onFocus={clearTimer}
        onBlur={scheduleClose}
        onEscapeKeyDown={(event) => {
          event.preventDefault()
          rowRef.current?.querySelector<HTMLAnchorElement>("a")?.focus()
          close()
        }}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
        }}
      >
        <p className="px-2 py-1.5 text-xs font-medium text-coval-text-muted">
          Starred dashboards
        </p>
        {error ? (
          <div className="px-2 py-2 text-sm">
            Could not load dashboards.
            <Button variant="ghost" onClick={onRetry}>
              Retry
            </Button>
          </div>
        ) : isLoading ? (
          <p className="px-2 py-2 text-sm text-coval-text-muted">Loading…</p>
        ) : favorites.length ? (
          favorites.map((dashboard) => (
            <Link
              key={dashboard.id}
              href={getHref(`/dashboard/${dashboard.id}`)}
              onClick={close}
              className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-surface-default-hover"
            >
              <Star className="size-3.5 shrink-0 fill-current text-coval-warning-text" />
              <span className="truncate">
                {dashboard.display_name || "Untitled"}
              </span>
            </Link>
          ))
        ) : (
          <p className="px-2 py-2 text-sm text-coval-text-muted">
            Star dashboards to keep them here.
          </p>
        )}
        <Link
          href={item.href}
          onClick={close}
          className="mt-1 block rounded-sm border-t border-coval-border px-2 py-1.5 text-sm font-medium hover:bg-surface-default-hover"
        >
          View all dashboards
        </Link>
      </PopoverContent>
    </Popover>
  )
}
